import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Bounds, MixerStateMove, MultiSelectElement, Node, Point, SaveObj } from '../../core/model/datatypes';
import { FileService } from '../../core/provider/file.service';
import { StateService } from '../../core/provider/state.service';
import { TreeService } from '../../core/provider/tree.service';
import { ZoomService } from '../../core/provider/zoom.service';

@Injectable({
  providedIn: 'root'
})



/**
 * shift + click on any subdraft or operaiton adds/removes that cponent from the selected list
 * shift + drag on teh palette enables selection of all elements within the bounds
 */
export class MultiselectService {
  private tree = inject(TreeService);
  private fs = inject(FileService);
  private zs = inject(ZoomService);
  private ss = inject(StateService);

  selected: Array<MultiSelectElement> = [];
  relative_position: Point = { x: 0, y: 0 };
  relative_position_before: Point = { x: 0, y: 0 };
  copy: SaveObj | null = null;
  moving_id: number = -1;




  multi_select_bounds: Bounds = { topleft: { x: 0, y: 0 }, width: 0, height: 0 };
  hasMultiSelectBounds: boolean = false;



  /**
   * publish the event when something changes in the select list. 
   */
  multiSelectListChange$: BehaviorSubject<Array<number>> = new BehaviorSubject<Array<number>>([]);
  multiSelectMoveElements$: BehaviorSubject<Array<{ id: number, topleft: Point }>> = new BehaviorSubject<Array<{ id: number, topleft: Point }>>([]);


  private selected_before: Array<{ id: number, topleft: Point }> = [];


  /**
   * when drag starts, we need to store a copy of the current state so we can undo it later
   */
  dragStart(id: number) {
    console.log("MULTI SELECT DRAG START", id);
    this.moving_id = id;
    this.selected_before = [];
    this.selected.forEach(el => {
      this.selected_before.push({ id: el.id, topleft: { x: el.topleft.x, y: el.topleft.y } });
    });
    this.relative_position_before = { x: this.relative_position.x, y: this.relative_position.y };
  }

  /**
   * the position of the moving element
   * @param cur_pos 
   */
  dragMove(cur_pos: Point) {

    const diff: Point = { x: cur_pos.x - this.relative_position.x, y: cur_pos.y - this.relative_position.y };


    this.selected.forEach(sel => {

      if (sel.id !== this.moving_id) {
        let updatedPos = this.getNewPosition(sel.id, diff);
        sel.positionUpdate.next(updatedPos)
      }
    });

  }

  /**
   * when drag ends, we need to apply the changes to the state
   */
  dragEnd() {
    const change: MixerStateMove = {
      originator: 'MIXER',
      type: 'MOVE',
      moving_id: this.moving_id,
      relative_position_before: this.relative_position_before,
      relative_position_after: this.relative_position,
      selected_before: this.selected_before,
      selected_after: this.selected
    }
    this.ss.addStateChange(change);
    this.moving_id = -1;
    this.selected_before = [];
    this.relative_position = { x: 0, y: 0 };
  }



  setRelativePosition(point: Point) {
    this.relative_position = point;
  }

  getRelativePosition(): Point {
    return { x: this.relative_position.x, y: this.relative_position.y };
  }

  setPosition(id: number, tl: Point) {
    const el = this.selected.find(el => el.id === id);
    if (el !== undefined) {
      el.topleft = { x: tl.x, y: tl.y };
    }
  }

  /**
   * toggle selection will add a selected element, and if its an op, all the child subdrafts, to the selected list
   * @param id the id of the element you are toggling
   * @param topleft the topleft point of this element, used to update position if one selected element moves
   */
  toggleSelection(id: number, topleft: Point): boolean {

    if (this.selected.find(el => el.id == id) !== undefined) {

      this.selected = this.selected.filter(el => el.id != id);
      this.multiSelectListChange$.next(this.selected.map(el => el.id));


      return false;
    } else {

      this.selected.push({ id, topleft, positionUpdate: new BehaviorSubject<Point>(topleft) });
      this.multiSelectListChange$.next(this.selected.map(el => el.id));

      return true;
    }
  }


  addSelection(id: number, topleft: Point) {
    if (this.selected.find(el => el.id == id) === undefined) {
      this.selected.push({ id, topleft, positionUpdate: new BehaviorSubject<Point>(topleft) });
      this.multiSelectListChange$.next(this.selected.map(el => el.id));
    }
  }


  removeSelection(id: number) {
    this.selected = this.selected.filter(el => el.id != id);
    this.multiSelectListChange$.next(this.selected.map(el => el.id));
  }


  clearSelections() {
    //clear all styles
    this.selected = [];
    this.multiSelectListChange$.next([]);
    this.copy = null;
  }

  isSelected(id: number): boolean {
    const f = this.selected.find(el => el.id == id);
    if (f === undefined) return false;
    else return true;
  }

  getSelections(): Array<number> {
    return this.selected.map(el => el.id);
  }

  getNewPosition(id: number, diff: Point) {
    const f = this.selected.find(el => el.id == id);
    if (f) {
      return { x: f.topleft.x + diff.x, y: f.topleft.y + diff.y }
    }
    return { x: 0, y: 0 };
  }


  /**
   * creates a copy of each of the elements (and their positions, for pasting into this file or another file)
   * @returns 
   */
  copySelections(): Promise<SaveObj> {

    let selected_nodes = this.selected
      .map(el => this.tree.getNode(el.id))
      .filter(el => el !== null && el !== undefined && el?.type !== 'cxn') //filter out connections because we will add these in later

    let node_mirror = selected_nodes.slice();

    let relevant_connection_ids: Array<number> = [];
    let relevant_connection_nodes: Array<Node> = [];

    selected_nodes.forEach(node => {

      node_mirror = node_mirror.filter(el => el !== null && el !== undefined && el?.id !== node?.id);

      node_mirror.forEach(mirror => {
        let cxns = this.tree.getConnectionsBetween(node?.id ?? -1, mirror?.id ?? -1);

        cxns.forEach(cxn => {
          if (cxn !== -1 && relevant_connection_ids.find(el => el == cxn) === undefined) relevant_connection_ids.push(cxn)
        })
      });

    });

    relevant_connection_nodes = relevant_connection_ids.map(el => this.tree.getNode(el)).filter(el => el !== null && el !== undefined);
    let all_nodes = selected_nodes.concat(relevant_connection_nodes);


    return this.fs.saver.copy(all_nodes.map(el => el?.id ?? -1))
      .then(ada => {
        this.copy = ada;
        return Promise.resolve(ada);
      });



  }






}
